/**
 * Deviitor Voice portfolio embed — floating assistant CTA + chat/debug toggles.
 * Usage: DeviitorVoice.init({ layout: 'floating', source: 'portfolio' });
 *
 * Voice client is bundled into this file so the portfolio does not need a
 * cross-origin ES module import (which requires CORS on voice-client.js).
 */
import { BUILD_VERSION } from './version.js';
import { DeviitorVoiceClient } from './voice-client.js';

(function () {
  const DEFAULT_ORIGIN = 'https://voice.dilhara.deviitor.com';
  const CTA_LABEL = 'Ask from AI Assistant';
  const LIVE_LABEL = 'Speak freely';

  const ICONS = {
    assistant:
      '<svg class="dv-cta-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">' +
      '<path fill="currentColor" d="M4 4.75A2.75 2.75 0 0 1 6.75 2h10.5A2.75 2.75 0 0 1 20 4.75v8.5A2.75 2.75 0 0 1 17.25 16H11l-3.8 3.04A.75.75 0 0 1 6 18.45V16H6.75A2.75 2.75 0 0 1 4 13.25v-8.5Z"/>' +
      '<path fill="currentColor" d="M17.2 2.4l.55 1.35 1.35.55-1.35.55-.55 1.35-.55-1.35-1.35-.55 1.35-.55.55-1.35Z"/>' +
      '</svg>',
    mic:
      '<svg class="dv-cta-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">' +
      '<path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/>' +
      '</svg>',
    chat: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z"/></svg>',
    debug:
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M9 3h6v2H9V3zm1 4h4a4 4 0 0 1 4 4v1h2v2h-2v1a4 4 0 0 1-1.2 2.8l1.5 1.5-1.4 1.4-1.6-1.6A3.96 3.96 0 0 1 13 18v2h-2v-2a3.96 3.96 0 0 1-2.3-1.1l-1.6 1.6-1.4-1.4 1.5-1.5A4 4 0 0 1 6 15v-1H4v-2h2v-1a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-4z"/></svg>',
    mute: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M12 3a3 3 0 0 0-3 3v5.17l8.84 8.84A6.96 6.96 0 0 0 19 12h-2a5 5 0 0 1-2.05 3.98l-1.4-1.4A3 3 0 0 0 15 11V6a3 3 0 0 0-3-3zm-9.41.59L1.17 4.99l5.54 5.54A6.94 6.94 0 0 0 5 12H3c0 3.53 2.61 6.43 6 6.92V22h2v-3.08c.74-.1 1.44-.32 2.09-.64l3.33 3.33 1.41-1.41L2.59 3.59z"/></svg>',
    stop: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M6 6h12v12H6z"/></svg>',
  };

  function ensureUi() {
    if (document.getElementById('deviitor-voice-root')) return;

    const root = document.createElement('div');
    root.id = 'deviitor-voice-root';
    root.innerHTML =
      '<aside id="dv-chat" class="dv-panel dv-chat" hidden aria-label="Conversation transcript">' +
      '<div class="dv-panel-head"><span>Conversation</span>' +
      '<button type="button" class="dv-panel-close" data-close="chat" aria-label="Close chat">×</button></div>' +
      '<div id="dv-chat-log" class="dv-chat-log">' +
      '<div class="dv-empty" id="dv-chat-empty">Transcripts appear here once you start talking.</div>' +
      '</div></aside>' +
      '<aside id="dv-debug" class="dv-panel dv-debug" hidden aria-label="Debug logs">' +
      '<div class="dv-panel-head"><span>Debug</span>' +
      '<button type="button" class="dv-panel-close" data-close="debug" aria-label="Close debug">×</button></div>' +
      '<pre id="dv-debug-log" class="dv-debug-log"></pre></aside>' +
      '<div class="dv-rail" aria-label="Voice assistant controls">' +
      '<div id="dv-session-tools" class="dv-session-tools" hidden>' +
      '<button type="button" id="dv-chat-btn" class="dv-fab dv-fab-secondary" aria-label="Toggle chat" title="Chat">' +
      ICONS.chat +
      '</button>' +
      '<button type="button" id="dv-debug-btn" class="dv-fab dv-fab-secondary" aria-label="Toggle debug" title="Debug">' +
      ICONS.debug +
      '</button>' +
      '<button type="button" id="dv-mute-btn" class="dv-fab dv-fab-secondary" aria-label="Mute" title="Mute" aria-pressed="false">' +
      ICONS.mute +
      '</button>' +
      '<button type="button" id="dv-stop-btn" class="dv-fab dv-fab-secondary" aria-label="Stop call" title="Stop">' +
      ICONS.stop +
      '</button>' +
      '</div>' +
      '<button type="button" id="dv-mic-btn" class="dv-fab dv-fab-mic dv-assistant-cta" aria-label="' +
      CTA_LABEL +
      '" title="' +
      CTA_LABEL +
      '">' +
      ICONS.assistant +
      '<span class="dv-cta-label">' +
      CTA_LABEL +
      '</span>' +
      '</button>' +
      '<div id="dv-status" class="dv-status">Tap to start</div>' +
      '<div class="dv-build">v' +
      BUILD_VERSION +
      '</div>' +
      '</div>' +
      '<div id="dv-mic-modal" class="dv-modal" hidden role="dialog" aria-modal="true">' +
      '<div class="dv-modal-card">' +
      '<h2>Microphone needed</h2>' +
      '<p id="dv-mic-modal-body">Allow microphone access to start the voice session.</p>' +
      '<div class="dv-modal-actions">' +
      '<button type="button" id="dv-mic-retry">Try again</button>' +
      '<button type="button" id="dv-mic-dismiss" class="dv-secondary">Close</button>' +
      '</div></div></div>';

    const style = document.createElement('style');
    style.textContent = [
      '#deviitor-voice-root{',
      '--dv-accent:var(--brown,#2563eb);',
      '--dv-accent-hover:var(--brown-hover,#1d4ed8);',
      '--dv-bg:var(--bg,#e2e2e0);',
      '--dv-panel:var(--bg-elevated,#eeeeec);',
      '--dv-soft:var(--bg-soft,#c8c8c4);',
      '--dv-ink:var(--ink,#1f1f1f);',
      '--dv-muted:var(--muted,#6a6a6a);',
      '--dv-white:var(--white,#fff);',
      '--dv-radius:var(--radius-xl,24px);',
      '--dv-ease:var(--ease,cubic-bezier(0.22,1,0.36,1));',
      '--dv-font:var(--font-sans,Inter,system-ui,sans-serif);',
      'font-family:var(--dv-font);color:var(--dv-ink);',
      '}',
      '#deviitor-voice-root *{box-sizing:border-box}',
      '#deviitor-voice-root [hidden]{display:none!important}',
      /* Clear of portfolio chrome; CTA is a labeled pill, not a mic-only FAB. */
      '#deviitor-voice-root .dv-rail{position:fixed;z-index:99999;right:20px;bottom:20px;display:flex;flex-direction:column;align-items:flex-end;gap:10px;width:auto;pointer-events:none}',
      '#deviitor-voice-root .dv-rail>*{pointer-events:auto}',
      '#deviitor-voice-root .dv-session-tools{display:flex;flex-direction:column;align-items:flex-end;gap:10px}',
      '#deviitor-voice-root .dv-fab{width:48px;height:48px;border-radius:50%;border:0;display:inline-grid;place-items:center;cursor:pointer;flex:0 0 auto;padding:0;line-height:0;transition:transform .18s var(--dv-ease),background .18s var(--dv-ease),box-shadow .18s var(--dv-ease);box-shadow:0 10px 28px color-mix(in srgb,var(--dv-ink) 18%,transparent)}',
      '#deviitor-voice-root .dv-fab svg{display:block;flex-shrink:0}',
      '#deviitor-voice-root .dv-fab:hover{transform:translateY(-1px)}',
      '#deviitor-voice-root .dv-fab-mic.dv-assistant-cta{width:auto;min-height:52px;height:auto;border-radius:999px;padding:12px 18px 12px 14px;display:inline-flex;align-items:center;gap:10px;background:var(--dv-accent);color:var(--dv-white);line-height:1.2;box-shadow:0 12px 32px color-mix(in srgb,var(--dv-accent) 35%,transparent)}',
      '#deviitor-voice-root .dv-fab-mic.dv-assistant-cta .dv-cta-label{font-size:13.5px;font-weight:650;letter-spacing:.01em;white-space:nowrap}',
      '#deviitor-voice-root .dv-fab-mic.dv-assistant-cta .dv-cta-icon{flex:0 0 auto}',
      '#deviitor-voice-root .dv-fab-mic:hover{background:var(--dv-accent-hover)}',
      '#deviitor-voice-root .dv-fab-mic.dv-live{box-shadow:0 0 0 4px color-mix(in srgb,var(--dv-accent) 28%,transparent),0 12px 30px color-mix(in srgb,var(--dv-accent) 35%,transparent)}',
      '#deviitor-voice-root .dv-fab-mic:disabled{opacity:.55;cursor:wait;transform:none}',
      '#deviitor-voice-root .dv-fab-secondary{background:var(--dv-panel);color:var(--dv-ink);border:1px solid color-mix(in srgb,var(--dv-ink) 10%,transparent)}',
      '#deviitor-voice-root .dv-fab-secondary.open,#deviitor-voice-root .dv-fab-secondary.muted{background:var(--dv-accent);color:var(--dv-white);border-color:transparent}',
      '#deviitor-voice-root .dv-status{text-align:right;font-size:12px;line-height:1.35;color:var(--dv-muted);max-width:220px;min-height:1.35em}',
      '#deviitor-voice-root .dv-build{display:none}',
      /* Keep page content clear of the floating pill on host sites. */
      'body:has(#deviitor-voice-root) main.main{padding-bottom:max(160px,calc(7.5rem + env(safe-area-inset-bottom,0px)))!important}',
      '@media (max-width:960px){',
      'body:has(#deviitor-voice-root) main.main{padding-bottom:max(150px,calc(7rem + env(safe-area-inset-bottom,0px)))!important}',
      '}',
      '@media (max-width:420px){',
      '#deviitor-voice-root .dv-fab-mic.dv-assistant-cta{max-width:calc(100vw - 40px);padding:11px 14px 11px 12px;gap:8px}',
      '#deviitor-voice-root .dv-fab-mic.dv-assistant-cta .dv-cta-label{font-size:12.5px;white-space:normal;text-align:left;max-width:11.5rem}',
      '}',
      '#deviitor-voice-root .dv-panel{position:fixed;z-index:99998;top:5.5rem;bottom:1.25rem;width:min(340px,calc(100vw - 5.5rem));background:color-mix(in srgb,var(--dv-panel) 94%,transparent);backdrop-filter:blur(10px);border:1px solid color-mix(in srgb,var(--dv-ink) 10%,transparent);border-radius:var(--dv-radius);box-shadow:0 18px 50px color-mix(in srgb,var(--dv-ink) 16%,transparent);display:flex;flex-direction:column;overflow:hidden}',
      '#deviitor-voice-root .dv-chat{left:1rem}',
      '#deviitor-voice-root .dv-debug{right:5.5rem}',
      '#deviitor-voice-root .dv-panel-head{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid color-mix(in srgb,var(--dv-ink) 8%,transparent);font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--dv-muted)}',
      '#deviitor-voice-root .dv-panel-close{border:0;background:transparent;color:var(--dv-muted);font-size:20px;line-height:1;cursor:pointer}',
      '#deviitor-voice-root .dv-chat-log{flex:1;overflow:auto;padding:12px;display:flex;flex-direction:column;gap:10px}',
      '#deviitor-voice-root .dv-empty{border:1px dashed color-mix(in srgb,var(--dv-ink) 14%,transparent);border-radius:14px;padding:12px;color:var(--dv-muted);font-size:13px}',
      '#deviitor-voice-root .dv-bubble{max-width:92%;padding:10px 12px;border-radius:14px;font-size:13.5px;line-height:1.45;box-shadow:0 8px 20px color-mix(in srgb,var(--dv-ink) 8%,transparent)}',
      '#deviitor-voice-root .dv-bubble .who{display:block;font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.7;margin-bottom:4px}',
      '#deviitor-voice-root .dv-bubble.user{align-self:flex-end;background:var(--dv-accent);color:var(--dv-white);border-bottom-right-radius:5px}',
      '#deviitor-voice-root .dv-bubble.agent{align-self:flex-start;background:var(--dv-soft);color:var(--dv-ink);border-bottom-left-radius:5px}',
      '#deviitor-voice-root .dv-bubble.links,#deviitor-voice-root .dv-bubble.summary{align-self:flex-start;background:var(--dv-panel);border:1px solid color-mix(in srgb,var(--dv-ink) 10%,transparent)}',
      '#deviitor-voice-root .dv-bubble.summary{padding:12px 14px}',
      '#deviitor-voice-root .dv-summary-title{font-weight:700;margin:.15rem 0 .55rem;font-size:.9rem}',
      '#deviitor-voice-root .dv-summary-grid{display:grid;gap:8px;margin:0}',
      '#deviitor-voice-root .dv-summary-grid>div{display:grid;grid-template-columns:5.6rem 1fr;gap:6px;align-items:start;font-size:12.5px;line-height:1.4}',
      '#deviitor-voice-root .dv-summary-grid dt{margin:0;color:var(--dv-muted);font-size:10px;text-transform:uppercase;letter-spacing:.04em;font-weight:700;padding-top:2px}',
      '#deviitor-voice-root .dv-summary-grid dd{margin:0;word-break:break-word}',
      '#deviitor-voice-root .dv-summary-footer{margin-top:10px;opacity:.9;font-size:12.5px;line-height:1.4}',
      '#deviitor-voice-root .dv-bubble a{color:var(--dv-accent);font-weight:600;text-decoration:underline;text-underline-offset:2px;word-break:break-all}',
      '#deviitor-voice-root .dv-link-card{display:flex;flex-direction:column;gap:8px}',
      '#deviitor-voice-root .dv-link-card a{display:flex;flex-direction:column;gap:2px;padding:10px 12px;border-radius:12px;background:color-mix(in srgb,var(--dv-accent) 10%,var(--dv-panel));color:var(--dv-ink);text-decoration:none;border:1px solid color-mix(in srgb,var(--dv-accent) 22%,transparent);font-size:12.5px;line-height:1.35;word-break:break-all}',
      '#deviitor-voice-root .dv-link-card a:hover{background:color-mix(in srgb,var(--dv-accent) 18%,var(--dv-panel))}',
      '#deviitor-voice-root .dv-link-card .link-label{font-weight:700;font-size:12px;color:var(--dv-accent)}',
      '#deviitor-voice-root .dv-debug-log{flex:1;margin:0;padding:12px;overflow:auto;white-space:pre-wrap;word-break:break-word;font:11.5px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;color:color-mix(in srgb,var(--dv-ink) 82%,transparent)}',
      '#deviitor-voice-root .dv-modal{position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:20px;background:color-mix(in srgb,var(--dv-ink) 45%,transparent);backdrop-filter:blur(5px)}',
      '#deviitor-voice-root .dv-modal-card{width:min(100%,22rem);background:var(--dv-panel);color:var(--dv-ink);border-radius:18px;padding:18px;border:1px solid color-mix(in srgb,var(--dv-ink) 10%,transparent);box-shadow:0 24px 60px color-mix(in srgb,var(--dv-ink) 22%,transparent)}',
      '#deviitor-voice-root .dv-modal-card h2{margin:0;font-size:1.05rem}',
      '#deviitor-voice-root .dv-modal-card p{margin:.65rem 0 0;color:var(--dv-muted);font-size:.9rem;line-height:1.45}',
      '#deviitor-voice-root .dv-modal-actions{display:flex;gap:8px;margin-top:1rem}',
      '#deviitor-voice-root .dv-modal-actions button{flex:1;border:0;border-radius:999px;padding:10px 12px;font-weight:650;cursor:pointer;background:var(--dv-accent);color:var(--dv-white)}',
      '#deviitor-voice-root .dv-modal-actions .dv-secondary{background:var(--dv-soft);color:var(--dv-ink)}',
      '@media (max-width:900px){',
      '#deviitor-voice-root .dv-chat,#deviitor-voice-root .dv-debug{top:auto;bottom:calc(160px + env(safe-area-inset-bottom,0px));height:36vh;width:min(320px,calc(100vw - 1.5rem))}',
      '#deviitor-voice-root .dv-chat{left:.75rem}',
      '#deviitor-voice-root .dv-debug{right:.75rem;left:auto}',
      '#deviitor-voice-root .dv-rail{bottom:calc(20px + env(safe-area-inset-bottom,0px))}',
      '}',
    ].join('');

    document.head.appendChild(style);
    document.body.appendChild(root);
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  window.DeviitorVoice = {
    version: BUILD_VERSION,
    _booted: false,
    init: async function (options) {
      if (window.DeviitorVoice._booted) return;
      window.DeviitorVoice._booted = true;

      const opts = options || {};
      const origin = (opts.origin || DEFAULT_ORIGIN).replace(/\/$/, '');
      const source = opts.source || 'portfolio';
      ensureUi();

      const micBtn = document.getElementById('dv-mic-btn');
      const sessionTools = document.getElementById('dv-session-tools');
      const chatBtn = document.getElementById('dv-chat-btn');
      const debugBtn = document.getElementById('dv-debug-btn');
      const muteBtn = document.getElementById('dv-mute-btn');
      const stopBtn = document.getElementById('dv-stop-btn');
      const statusEl = document.getElementById('dv-status');
      const chatPanel = document.getElementById('dv-chat');
      const debugPanel = document.getElementById('dv-debug');
      const chatLog = document.getElementById('dv-chat-log');
      const debugLog = document.getElementById('dv-debug-log');
      const micModal = document.getElementById('dv-mic-modal');
      const micModalBody = document.getElementById('dv-mic-modal-body');
      const micRetry = document.getElementById('dv-mic-retry');
      const micDismiss = document.getElementById('dv-mic-dismiss');

      if (!micBtn || !statusEl) {
        console.error('[DeviitorVoice] UI failed to mount');
        window.DeviitorVoice._booted = false;
        return;
      }

      let sessionMeta = null;
      let client = null;
      let expiryTimer = null;
      let connected = false;
      let starting = false;

      function setStatus(text) {
        statusEl.textContent = text;
      }

      function log(line) {
        const stamp = new Date().toLocaleTimeString();
        debugLog.textContent = '[' + stamp + '] ' + line + '\n' + debugLog.textContent;
      }

      function showMicModal(message) {
        micModalBody.textContent =
          message ||
          'Allow microphone access to start the voice session. If blocked, enable it in browser settings.';
        micModal.hidden = false;
      }

      function hideMicModal() {
        micModal.hidden = true;
      }

      function setConnectedUi(on) {
        connected = on;
        micBtn.classList.toggle('dv-live', on);
        sessionTools.hidden = !on;
        const iconEl = micBtn.querySelector('.dv-cta-icon');
        const labelEl = micBtn.querySelector('.dv-cta-label');
        if (on) {
          if (iconEl) iconEl.outerHTML = ICONS.mic;
          if (labelEl) labelEl.textContent = LIVE_LABEL;
          micBtn.title = LIVE_LABEL + ' — tap to mute';
          micBtn.setAttribute('aria-label', LIVE_LABEL + '. Tap to mute');
          setStatus(LIVE_LABEL);
        } else {
          if (iconEl) iconEl.outerHTML = ICONS.assistant;
          else micBtn.insertAdjacentHTML('afterbegin', ICONS.assistant);
          if (labelEl) labelEl.textContent = CTA_LABEL;
          micBtn.title = CTA_LABEL;
          micBtn.setAttribute('aria-label', CTA_LABEL);
          chatPanel.hidden = true;
          debugPanel.hidden = true;
          chatBtn.classList.remove('open');
          debugBtn.classList.remove('open');
          muteBtn.classList.remove('muted');
          muteBtn.setAttribute('aria-pressed', 'false');
          muteBtn.title = 'Mute';
        }
      }

      function clearChat() {
        shownLinkKeys.clear();
        lastMentionedProjectUrl = null;
        lastBookingResult = null;
        chatLog.innerHTML =
          '<div class="dv-empty" id="dv-chat-empty">Transcripts appear here once you start talking.</div>';
      }

      const FALLBACK_BOOKING_URL = 'https://calendar.app.google/983Vkg8WixeY8yWf6';
      const PORTFOLIO_URL = 'https://dilhara.deviitor.com';
      const PORTFOLIO_WORK_URL = 'https://dilhara.deviitor.com/work';
      const PROJECT_LINKS = [
        {
          keys: [/flood/i, /early.?warning/i, /river/i],
          label: 'Flood Early Warning case study',
          url: 'https://dilhara.deviitor.com/work/flood-early-warning',
        },
        {
          keys: [/grands.?m[eè]res/i, /grands-meres/i, /natural.?remed/i],
          label: 'Grands-Meres case study',
          url: 'https://dilhara.deviitor.com/work/grands-meres',
        },
        {
          keys: [/wrestling/i, /wrestlingworld/i],
          label: 'WrestlingWorld case study',
          url: 'https://dilhara.deviitor.com/work/wrestlingworld',
          live: { label: 'Live site', url: 'https://wrestlingworld.co' },
        },
        {
          keys: [/better.?move/i, /kuwaiti.?mov/i],
          label: 'Better Move case study',
          url: 'https://dilhara.deviitor.com/work/better-move',
          live: { label: 'Live site', url: 'https://bettermoveco.com' },
        },
      ];
      const shownLinkKeys = new Set();
      let lastMentionedProjectUrl = null;
      /** Successful create_booking payload — used to reshare Meet/event links, never the public page. */
      let lastBookingResult = null;

      function addLinksCard(opts) {
        const title = opts.title;
        const links = opts.links;
        const key = opts.key;
        const force = !!opts.force;
        const usable = (links || []).filter(function (l) {
          return l && l.url;
        });
        if (!usable.length) return false;
        const cardKey = key || usable.map(function (l) { return l.url; }).join('|');
        if (!force && shownLinkKeys.has(cardKey)) return true;
        shownLinkKeys.add(cardKey);

        const empty = document.getElementById('dv-chat-empty');
        if (empty) empty.remove();
        const el = document.createElement('div');
        el.className = 'dv-bubble agent links';
        el.dataset.linksCard = cardKey;
        const items = usable
          .map(function (l) {
            return (
              '<a href="' +
              escapeHtml(l.url) +
              '" target="_blank" rel="noopener noreferrer">' +
              '<span class="link-label">' +
              escapeHtml(l.label) +
              '</span>' +
              escapeHtml(String(l.url).replace(/^https?:\/\//i, '')) +
              '</a>'
            );
          })
          .join('');
        el.innerHTML =
          '<span class="who">Links</span>' +
          '<div class="dv-summary-title">' +
          escapeHtml(title || 'Useful links') +
          '</div>' +
          '<div class="dv-link-card">' +
          items +
          '</div>';
        chatLog.appendChild(el);
        chatLog.scrollTop = chatLog.scrollHeight;
        // Keep chat panel open when links arrive.
        if (chatPanel.hidden) togglePanel('chat', true);
        return true;
      }

      function bookingLinksPresentInChat(meetLink, htmlLink) {
        const hrefs = Array.prototype.slice
          .call(chatLog.querySelectorAll('.dv-link-card a'))
          .map(function (a) {
            return a.getAttribute('href') || '';
          })
          .filter(Boolean);
        const hasMeet = meetLink
          ? hrefs.some(function (h) {
              return h === meetLink || /meet\.google\.com/i.test(h);
            })
          : false;
        const hasCal = htmlLink
          ? hrefs.some(function (h) {
              return (
                h === htmlLink ||
                /calendar\.google\.com|google\.com\/calendar/i.test(h)
              );
            })
          : false;
        return !!(hasMeet && hasCal);
      }

      function removeBookingLinkCards() {
        Array.prototype.slice
          .call(chatLog.querySelectorAll('.dv-bubble.links[data-links-card]'))
          .forEach(function (el) {
            const key = el.getAttribute('data-links-card') || '';
            if (key.indexOf('booking-') === 0 || key === 'fallback-booking') el.remove();
          });
        Array.from(shownLinkKeys).forEach(function (key) {
          if (String(key).indexOf('booking-') === 0 || key === 'fallback-booking') {
            shownLinkKeys.delete(key);
          }
        });
      }

      function addFallbackBookingCard(force) {
        // Never replace a successful booking's Meet/event links with the public scheduling page.
        if (lastBookingResult && lastBookingResult.ok) return false;
        return addLinksCard({
          key: 'fallback-booking',
          title: 'Booking',
          links: [{ label: 'Book a call', url: FALLBACK_BOOKING_URL }],
          force: !!force,
        });
      }

      async function callFunctionApi(name, args) {
        const res = await fetch(origin + '/api/functions/' + name, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: sessionMeta.sessionAuth,
            'X-Session-Id': sessionMeta.sessionId,
          },
          body: JSON.stringify(args),
        });
        return res.json();
      }

      async function settleChatDom() {
        await new Promise(function (r) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              r();
            });
          });
        });
        await new Promise(function (r) {
          setTimeout(r, 80);
        });
      }

      /**
       * Loopback: put Meet + calendar EVENT buttons in chat (not the public booking page),
       * refresh if missing, only then mark links_ready / links_in_chat for the agent.
       */
      async function ensureBookingLinksInChat(result) {
        let current = Object.assign({}, result, {
          links_ready: false,
          links_in_chat: false,
        });
        lastBookingResult = current;
        const maxAttempts = 5;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          if (attempt > 0 && current.eventId) {
            log('Refreshing booking links (attempt ' + (attempt + 1) + ')…');
            try {
              const refreshed = await callFunctionApi('refresh_booking_links', {
                event_id: current.eventId,
              });
              if (refreshed && refreshed.ok) {
                current.meetLink = refreshed.meetLink || current.meetLink;
                current.htmlLink = refreshed.htmlLink || current.htmlLink;
              }
            } catch (err) {
              log(
                'refresh_booking_links failed: ' +
                  (err && err.message ? err.message : 'error'),
              );
            }
          }

          const links = [];
          if (current.meetLink) links.push({ label: 'Google Meet', url: current.meetLink });
          if (current.htmlLink) links.push({ label: 'Calendar event', url: current.htmlLink });

          if (links.length) {
            removeBookingLinkCards();
            addLinksCard({
              key: 'booking-' + (current.eventId || current.meetLink || 'links'),
              title: 'Your booking links',
              links: links,
              force: true,
            });
          }

          await settleChatDom();
          if (bookingLinksPresentInChat(current.meetLink, current.htmlLink)) {
            current.links_available = true;
            current.links_ready = true;
            current.links_in_chat = true;
            current.agent_speak =
              'You are booked. Google Meet and calendar EVENT links are confirmed in the chat. Say you shared those — never the public calendar.app.google page.';
            lastBookingResult = current;
            return current;
          }

          await new Promise(function (r) {
            setTimeout(r, 700);
          });
        }

        current.links_available = Boolean(current.meetLink && current.htmlLink);
        current.links_ready = false;
        current.links_in_chat = false;
        current.agent_speak =
          'You are booked, but Meet/calendar EVENT links are NOT confirmed in the chat yet. Say you are still placing them — do NOT claim they were already shared, and do NOT share the public calendar.app.google booking page.';
        lastBookingResult = current;
        return current;
      }

      function projectsInText(text) {
        const lower = String(text || '').toLowerCase();
        return PROJECT_LINKS.filter(function (p) {
          return (
            p.keys.some(function (re) {
              return re.test(text);
            }) || lower.indexOf(p.url.replace(/^https?:\/\//i, '').toLowerCase()) !== -1
          );
        });
      }

      function isBookingShareText(text) {
        return /google meet|calendar (?:event )?links?|you're booked|you are booked|booking (?:is )?confirm|shared .{0,40}booking links?/i.test(
          text,
        );
      }

      function isPublicBookingPageOffer(text) {
        return /calendar booking link|book a call through the calendar|public booking (?:page|link)|calendar\.app\.google/i.test(
          text,
        );
      }

      function isCaseStudyOffer(text) {
        return /case study/i.test(text) && /shared|link|chat/i.test(text);
      }

      function isPortfolioOffer(text) {
        return (
          /portfolio (?:link|website|site)|shared .{0,40}portfolio/i.test(text) ||
          /projects? page|all projects|dilhara'?s projects/i.test(text)
        );
      }

      function isLinkOffer(text) {
        return (
          isCaseStudyOffer(text) ||
          isPortfolioOffer(text) ||
          /shared.{0,80}(?:case study|portfolio|projects?)(?: link)?(?: in the chat)?/i.test(text)
        );
      }

      function maybeShareContextualLinks(role, content) {
        const text = String(content || '');
        if (!text.trim()) return;
        const isAgent = String(role || '').toLowerCase() !== 'user';
        const mentioned = projectsInText(text);
        if (mentioned.length === 1) lastMentionedProjectUrl = mentioned[0].url;
        if (!isAgent) return;

        const forceReshare = /again|once more|re-?send|check again|still (?:don'?t|do not) see/i.test(
          text,
        );

        // After a successful booking: reshare Meet + calendar EVENT links only.
        // Never inject the public calendar.app.google scheduling page.
        if (lastBookingResult && lastBookingResult.ok) {
          if (
            isBookingShareText(text) ||
            /shared .{0,60}(?:booking|meet|calendar)|booking links?/i.test(text) ||
            forceReshare
          ) {
            void ensureBookingLinksInChat(lastBookingResult);
          }
          return;
        }

        if (isBookingShareText(text)) return;

        const linkOffer = isLinkOffer(text);

        let toShare = [];
        if (mentioned.length === 1 && (isCaseStudyOffer(text) || /case study/i.test(text))) {
          toShare = mentioned;
        } else if (isCaseStudyOffer(text) && lastMentionedProjectUrl) {
          toShare = PROJECT_LINKS.filter(function (p) {
            return p.url === lastMentionedProjectUrl;
          });
        }

        for (let i = 0; i < toShare.length; i++) {
          const project = toShare[i];
          const links = [{ label: project.label, url: project.url }];
          if (project.live) links.push(project.live);
          addLinksCard({
            key: project.url,
            title: 'Case study',
            links: links,
            force: forceReshare || isCaseStudyOffer(text),
          });
        }

        if (toShare.length === 0 && linkOffer) {
          const wantsWork =
            /projects? page|all projects|dilhara'?s projects|\/work\b/i.test(text) ||
            (/shared .{0,50}projects?/i.test(text) && !/portfolio|case study/i.test(text));
          const wantsPortfolio = /portfolio (?:link|website|site)|shared .{0,40}portfolio/i.test(
            text,
          );
          if (wantsWork) {
            addLinksCard({
              key: 'portfolio-work',
              title: 'Projects',
              links: [{ label: 'All projects', url: PORTFOLIO_WORK_URL }],
              force: forceReshare || linkOffer,
            });
          } else if (wantsPortfolio) {
            addLinksCard({
              key: 'portfolio',
              title: 'Portfolio',
              links: [{ label: 'Website', url: PORTFOLIO_URL }],
              force: forceReshare || linkOffer,
            });
          } else if (mentioned.length > 1 && isPortfolioOffer(text)) {
            addLinksCard({
              key: 'portfolio-work',
              title: 'Projects',
              links: [{ label: 'All projects', url: PORTFOLIO_WORK_URL }],
              force: forceReshare || linkOffer,
            });
          }
        }

        // Public scheduling page — only when booking tools failed (no successful booking this session).
        if (isPublicBookingPageOffer(text)) {
          addFallbackBookingCard(forceReshare || linkOffer);
        }
      }

      function scrubSpokenUrls(text) {
        return String(text)
          .replace(/https?:\/\/meet\.google\.com\/[^\s]+/gi, '')
          .replace(/https?:\/\/(?:www\.)?google\.com\/calendar\/[^\s]+/gi, '')
          .replace(/https?:\/\/calendar\.app\.google\/[^\s]+/gi, '')
          .replace(/https?:\/\/dilhara\.deviitor\.com(?:\/[\w/-]*)?/gi, '')
          .replace(/\bdilhara\.deviitor\.com(?:\/[\w/-]*)?/gi, '')
          .replace(/\b(?:www\.)?wrestlingworld\.co\b/gi, '')
          .replace(/\b(?:www\.)?bettermoveco\.com\b/gi, '')
          .replace(/\bat\s+(?:dilhara\.deviitor\.com|wrestlingworld\.co|bettermoveco\.com)\b/gi, '')
          .replace(/\b(using this link|here's the link|here is the link)\s*:?\s*/gi, '')
          .replace(/\s{2,}/g, ' ')
          .replace(/\s+([.,!?])/g, '$1')
          .trim();
      }

      function normalizeSpokenEmail(text) {
        let out = String(text || '');
        out = out.replace(
          /\b([a-z0-9]+(?:\s+dot\s+[a-z0-9]+)+)\s+at\s+([a-z0-9][a-z0-9.\s-]*?)\s+dot\s+([a-z]{2,})\b/gi,
          function (_m, user, domain, tld) {
            return (
              String(user).replace(/\s+dot\s+/gi, '.').replace(/\s+/g, '') +
              '@' +
              String(domain).replace(/\s+/g, '') +
              '.' +
              tld
            );
          },
        );
        out = out.replace(
          /\b([a-z0-9][a-z0-9.\s_%+-]*?)\s+at\s+([a-z0-9][a-z0-9.\s-]*?)\s+dot\s+([a-z]{2,})\b/gi,
          function (_m, user, domain, tld) {
            return (
              String(user).replace(/\s+/g, '') +
              '@' +
              String(domain).replace(/\s+/g, '') +
              '.' +
              tld
            );
          },
        );
        out = out.replace(
          /\b([a-z0-9][a-z0-9.\s_%+-]*?)\s+at\s+([a-z0-9][a-z0-9.-]*\.[a-z]{2,})\b/gi,
          function (_m, user, domain) {
            return String(user).replace(/\s+/g, '') + '@' + domain;
          },
        );
        return out.replace(/\s+/g, ' ').trim();
      }

      function normalizeEmailFieldValue(value) {
        let email = normalizeSpokenEmail(String(value || '').trim());
        email = email.replace(/\s+/g, ' ').trim();
        if (/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(email)) return email;
        const spacedDomain = email.match(
          /^([a-z0-9._%+-]+)\s+([a-z0-9][a-z0-9.-]*\.[a-z]{2,})$/i,
        );
        if (spacedDomain) return spacedDomain[1] + '@' + spacedDomain[2];
        const dotCount = (email.match(/\bdot\b/gi) || []).length;
        if (dotCount === 1) {
          const loose = email.match(
            /^([a-z0-9][a-z0-9\s._%+-]*?)\s+([a-z0-9][a-z0-9\s-]*?)\s+dot\s+([a-z]{2,})$/i,
          );
          if (loose) {
            return (
              loose[1].replace(/\s+/g, '') + '@' + loose[2].replace(/\s+/g, '') + '.' + loose[3]
            );
          }
        }
        return email;
      }

      function linkifyHtml(escapedText) {
        return String(escapedText).replace(
          /\b([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})\b/gi,
          function (email) {
            return (
              '<a href="mailto:' +
              email +
              '" target="_blank" rel="noopener noreferrer">' +
              email +
              '</a>'
            );
          },
        );
      }

      function extractSummaryFields(text) {
        const src = String(text).replace(/\s+/g, ' ').trim();
        const labelRe =
          /\b(Date and time|Date|Time|Timezone|Name|Email|Purpose(?: of the call)?)\s*:\s*/gi;
        const matches = Array.from(src.matchAll(labelRe));
        if (matches.length < 2) return {};

        const fields = {};
        const keyMap = {
          date: 'when',
          time: 'when',
          'date and time': 'when',
          timezone: 'timezone',
          name: 'name',
          email: 'email',
          purpose: 'purpose',
          'purpose of the call': 'purpose',
        };

        for (let i = 0; i < matches.length; i++) {
          const label = matches[i][1].toLowerCase();
          const key = keyMap[label];
          if (!key || fields[key]) continue;
          const valueStart = matches[i].index + matches[i][0].length;
          const valueEnd = i + 1 < matches.length ? matches[i + 1].index : src.length;
          let value = src.slice(valueStart, valueEnd).trim();
          value = value
            .replace(
              /\s*(Does everything look good[\s\S]*|Would you like to change[\s\S]*|Please confirm[\s\S]*|What is the purpose[\s\S]*)$/i,
              '',
            )
            .replace(/^[-–—,:.\s]+|[-–—,:.\s]+$/g, '')
            .trim();
          if (value) fields[key] = value;
        }

        if (fields.email) {
          fields.email = normalizeEmailFieldValue(
            fields.email
              .replace(/\s*(What is the purpose[\s\S]*|Does everything look good[\s\S]*)$/i, '')
              .trim(),
          );
        }

        if (
          fields.purpose &&
          /^(what is the purpose|purpose of the call\??|please (tell|share)|can (i|you) (get|have))/i.test(
            fields.purpose,
          )
        ) {
          delete fields.purpose;
        }

        const footerMatch = src.match(
          /(Does everything look good[\s\S]*|Would you like to change[\s\S]*|Please confirm[\s\S]*)$/i,
        );
        if (footerMatch) fields.footer = footerMatch[1].trim();

        return fields;
      }

      function formatMessageHtml(raw) {
        const normalized = normalizeSpokenEmail(String(raw || ''))
          .replace(/\s+/g, ' ')
          .trim();
        const fields = extractSummaryFields(normalized);
        const summaryCue =
          /booking summary|summary for your booking|here'?s the summary|does everything look good/i.test(
            normalized,
          );
        const looksLikeSummary = Boolean(
          fields.when &&
            fields.name &&
            fields.email &&
            fields.purpose &&
            (fields.timezone || summaryCue),
        );

        if (looksLikeSummary) {
          const rows = [
            ['When', fields.when],
            ['Timezone', fields.timezone],
            ['Name', fields.name],
            ['Email', fields.email],
            ['Purpose', fields.purpose],
          ].filter(function (row) {
            return row[1];
          });
          const footer =
            fields.footer || 'Does everything look good, or would you like to change anything?';
          const grid = rows
            .map(function (row) {
              return (
                '<div><dt>' +
                escapeHtml(row[0]) +
                '</dt><dd>' +
                linkifyHtml(escapeHtml(row[1])) +
                '</dd></div>'
              );
            })
            .join('');
          return {
            summary: true,
            html:
              '<div class="dv-summary-title">Booking summary</div>' +
              '<dl class="dv-summary-grid">' +
              grid +
              '</dl>' +
              '<div class="dv-summary-footer">' +
              linkifyHtml(escapeHtml(footer)) +
              '</div>',
          };
        }

        return { summary: false, html: linkifyHtml(escapeHtml(normalized)) };
      }

      function addBubble(role, content) {
        if (!content || !String(content).trim()) return;
        const empty = document.getElementById('dv-chat-empty');
        if (empty) empty.remove();
        const isUser = String(role).toLowerCase() === 'user';

        // After a booking, user asks like "press the links" → reshare Meet + calendar EVENT only.
        if (
          isUser &&
          lastBookingResult &&
          lastBookingResult.ok &&
          /(?:press|show|share|send|where|need|open|check).{0,40}link|google meet|calendar event|booking links?/i.test(
            content,
          )
        ) {
          void ensureBookingLinksInChat(lastBookingResult);
        }

        maybeShareContextualLinks(role, content);
        const displayText = isUser ? content : scrubSpokenUrls(content);
        if (!isUser && !displayText) return;

        const formatted = isUser
          ? { summary: false, html: escapeHtml(displayText) }
          : formatMessageHtml(displayText);
        const el = document.createElement('div');
        el.className =
          'dv-bubble ' +
          (isUser ? 'user' : 'agent') +
          (formatted.summary ? ' summary' : '');
        el.innerHTML =
          '<span class="who">' +
          (isUser ? 'You' : 'Agent') +
          '</span>' +
          formatted.html;
        chatLog.appendChild(el);
        chatLog.scrollTop = chatLog.scrollHeight;
        if (formatted.summary && chatPanel.hidden) togglePanel('chat', true);
      }

      function togglePanel(which, force) {
        const panel = which === 'chat' ? chatPanel : debugPanel;
        const btn = which === 'chat' ? chatBtn : debugBtn;
        const open = typeof force === 'boolean' ? force : panel.hidden;
        panel.hidden = !open;
        btn.classList.toggle('open', open);
      }

      async function createSession() {
        const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await fetch(origin + '/api/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source: source, clientTimezone: clientTimezone }),
        });
        const data = await res.json().catch(function () {
          return {};
        });
        if (!res.ok) throw new Error(data.message || data.error || 'Session failed');
        return data;
      }

      async function mintToken() {
        const res = await fetch(origin + '/api/token', {
          method: 'POST',
          headers: {
            Authorization: sessionMeta.sessionAuth,
            'X-Session-Id': sessionMeta.sessionId,
          },
        });
        const data = await res.json().catch(function () {
          return {};
        });
        if (!res.ok) throw new Error(data.message || data.error || 'Token mint failed');
        return data.token;
      }

      async function endSession() {
        if (!sessionMeta) return;
        try {
          await fetch(origin + '/api/session/end', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: sessionMeta.sessionAuth,
              'X-Session-Id': sessionMeta.sessionId,
            },
            body: JSON.stringify({ sessionId: sessionMeta.sessionId }),
          });
        } catch (_) {}
      }

      async function handleFunctionCall(msg) {
        const items = msg.functions || [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          // Server-executed tools (e.g. get_available_slots) — Deepgram already has the result.
          if (item.client_side === false) {
            log('Skipping server-side function: ' + item.name);
            client.endFunctionCall();
            continue;
          }

          let args = {};
          try {
            args =
              typeof item.arguments === 'string'
                ? JSON.parse(item.arguments || '{}')
                : item.arguments || {};
          } catch (_) {
            args = {};
          }

          if (item.name === 'end_call') {
            const result = { ok: true, ended: true, reason: args.reason || 'end_call' };
            client.sendFunctionCallResponse(item.id, item.name, JSON.stringify(result));
            setStatus('Ending call…');
            log('end_call');
            client.endCallAfterConfirmation({
              reason: result.reason,
              onDone: function () {
                stopConversation('Call ended');
                endSession();
              },
            });
            continue;
          }

          try {
            setStatus(item.name === 'get_available_slots' ? 'Checking…' : 'Working…');
            log('Function: ' + item.name);
            let result = await client.runFunctionWithWatchdog(
              item.name,
              async function () {
                const raw = await fetch(origin + '/api/functions/' + item.name, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionMeta.sessionAuth,
                    'X-Session-Id': sessionMeta.sessionId,
                  },
                  body: JSON.stringify(args),
                }).then(function (r) {
                  return r.json();
                });
                // Loopback before the agent receives the tool result.
                if (item.name === 'create_booking' && raw && raw.ok) {
                  return ensureBookingLinksInChat(raw);
                }
                return raw;
              },
              { timeoutMs: item.name === 'create_booking' ? 35_000 : 12_000 },
            );

            if (item.name === 'create_booking' && result && result.ok) {
              log(
                'Booking links loopback → ready=' +
                  result.links_ready +
                  ' in_chat=' +
                  result.links_in_chat +
                  ' meet=' +
                  Boolean(result.meetLink) +
                  ' cal=' +
                  Boolean(result.htmlLink),
              );
            }

            client.sendFunctionCallResponse(item.id, item.name, JSON.stringify(result));
            setStatus('Listening…');

            if (
              item.name === 'get_available_slots' &&
              result &&
              result.ok === false &&
              /calendar\.app\.google|fallback/i.test(String(result.message || ''))
            ) {
              addFallbackBookingCard(false);
            }
            if (item.name === 'create_booking' && result && result.ok === false) {
              addFallbackBookingCard(false);
            }
          } catch (err) {
            client.sendFunctionCallResponse(
              item.id,
              item.name,
              JSON.stringify({
                ok: false,
                reason: 'api_error',
                message: err && err.message ? err.message : 'error',
              }),
            );
            setStatus('Listening…');
            log('Function error: ' + (err && err.message ? err.message : 'error'));
          }
        }
      }

      function stopConversation(statusText) {
        if (expiryTimer) clearTimeout(expiryTimer);
        expiryTimer = null;
        if (client) client.disconnect();
        client = null;
        starting = false;
        micBtn.disabled = false;
        setConnectedUi(false);
        setStatus(statusText || 'Tap to start');
      }

      async function startConversation() {
        if (connected || starting) return;
        starting = true;
        hideMicModal();
        micBtn.disabled = true;
        setStatus('Checking microphone…');
        log('Requesting microphone permission');
        debugLog.textContent = '';
        clearChat();

        let micStream = null;
        try {
          micStream = await DeviitorVoiceClient.ensureMicrophoneAccess();
        } catch (err) {
          starting = false;
          micBtn.disabled = false;
          const message = err && err.message ? err.message : 'Microphone permission required';
          setStatus(message);
          log('Microphone blocked: ' + message);
          showMicModal(message);
          return;
        }

        try {
          setStatus('Creating session…');
          sessionMeta = await createSession();
          log('Session ' + sessionMeta.sessionId);

          const ms = Date.parse(sessionMeta.expiresAt) - Date.now();
          expiryTimer = setTimeout(function () {
            stopConversation('Session ended (5 min)');
            endSession();
          }, Math.max(ms, 0));

          client = new DeviitorVoiceClient({
            tokenFactory: mintToken,
            agent: sessionMeta.agent,
            audio: sessionMeta.audio,
            inactivity: { warnMs: 90_000, endMs: 45_000 },
            onEvent: function (ev) {
              if (ev.type === 'connected') {
                setStatus('Connected…');
                log('WebSocket connected');
              }
              if (ev.type === 'settings-applied') {
                setStatus('Listening…');
                log('Settings applied');
              }
              if (ev.type === 'conversation-text') {
                addBubble(ev.role, ev.content);
                log((ev.role || '?') + ': ' + (ev.content || ''));
              }
              if (ev.type === 'function-call-request') {
                log('FunctionCallRequest');
                handleFunctionCall(ev);
              }
              if (ev.type === 'agent-thinking') log('Agent thinking…');
              if (ev.type === 'inactivity-warning') setStatus('Still there? Checking in…');
              if (ev.type === 'inactivity-cleared') setStatus('Listening…');
              if (ev.type === 'inactivity-ending') setStatus('No reply — ending…');
              if (ev.type === 'farewell-detected') {
                setStatus(ev.role === 'user' ? 'Wrapping up…' : 'Ending after goodbye…');
              }
              if (ev.type === 'mute-changed') {
                muteBtn.classList.toggle('muted', !!ev.muted);
                muteBtn.setAttribute('aria-pressed', ev.muted ? 'true' : 'false');
                muteBtn.title = ev.muted ? 'Unmute' : 'Mute';
                setStatus(ev.muted ? 'Muted' : 'Listening…');
                log(ev.muted ? 'Mic muted' : 'Mic unmuted');
              }
              if (ev.type === 'call-ending') setStatus('Wrapping up…');
              if (ev.type === 'call-ended') {
                endSession();
                stopConversation('Call ended');
              }
              if (ev.type === 'disconnected') {
                stopConversation('Disconnected');
                log('Disconnected: ' + (ev.reason || ''));
              }
              if (ev.type === 'error') {
                setStatus(ev.description || ev.message || 'Error');
                log('Error: ' + (ev.description || ev.message || JSON.stringify(ev)));
              }
              if (ev.type === 'warning') log('Warning: ' + (ev.description || JSON.stringify(ev)));
            },
          });

          setStatus('Connecting…');
          await client.connect();
          await client.startMicrophone(micStream);
          micStream = null;
          starting = false;
          micBtn.disabled = false;
          setConnectedUi(true);
          togglePanel('chat', true);
          setStatus('Listening…');
          log('Microphone started');
        } catch (err) {
          starting = false;
          micBtn.disabled = false;
          try {
            if (micStream) {
              micStream.getTracks().forEach(function (t) {
                t.stop();
              });
            }
          } catch (_) {}
          throw err;
        }
      }

      micBtn.addEventListener('click', function () {
        if (connected) {
          if (!client) return;
          client.toggleMute();
          return;
        }
        startConversation().catch(function (err) {
          starting = false;
          micBtn.disabled = false;
          setConnectedUi(false);
          setStatus(err.message || 'Failed');
          log(err.message || String(err));
        });
      });

      chatBtn.addEventListener('click', function () {
        togglePanel('chat');
      });
      debugBtn.addEventListener('click', function () {
        togglePanel('debug');
      });
      muteBtn.addEventListener('click', function () {
        if (!client) return;
        client.toggleMute();
      });
      stopBtn.addEventListener('click', function () {
        stopConversation('Stopped');
        endSession();
      });

      rootCloseHandlers();
      function rootCloseHandlers() {
        document.querySelectorAll('#deviitor-voice-root [data-close]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            togglePanel(btn.getAttribute('data-close'), false);
          });
        });
      }

      micRetry.addEventListener('click', function () {
        hideMicModal();
        startConversation().catch(function (err) {
          starting = false;
          micBtn.disabled = false;
          setStatus(err.message || 'Failed');
          log(err.message || String(err));
        });
      });
      micDismiss.addEventListener('click', hideMicModal);
      micModal.addEventListener('click', function (ev) {
        if (ev.target === micModal) hideMicModal();
      });

      window.addEventListener('beforeunload', function () {
        endSession();
      });

      log('Embed ready v' + BUILD_VERSION);
    },
  };
})();
